import {
  parseSmsSessionPolicyByGroup,
  resolveSmsSessionGroupPolicy,
  SmsSessionGroupPolicy,
  SmsSessionPolicyByGroup,
} from '@provision/conversation-types';
import { PartnerSettingValue } from '@provision/entity-types';
import { useMemo, useRef, useState } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { PartnerSetting, useManagePartnerSettingsValue } from 'src/graphql';

export type SettingRecord = Partial<PartnerSettingValue>;

/** Known only to the write queue while writes are pending; once it drains the rendered record is current. */
interface PendingWrites {
  chain: Promise<void>;
  count: number;
  /** The last queued value, so edits queued behind an in-flight write build on each other. */
  policy: SmsSessionPolicyByGroup | null;
  /** The row id a write learned: null = no row, undefined = the rendered record decides. */
  recordId: string | null | undefined;
}

/** A first edit copies the entry the group ran on, so a partner value holds only whole entries. */
export function useSmsSessionPolicy(records: SettingRecord[], partnerId: number | null) {
  const {
    createPartnerSettingsValue,
    updatePartnerSettingsValue,
    deletePartnerSettingsValue,
  } = useManagePartnerSettingsValue(partnerId);
  const pending = useRef<PendingWrites>({
    chain: Promise.resolve(),
    count: 0,
    policy: null,
    recordId: undefined,
  });
  // The queued value as state, so fields mounted while writes are pending show what will be saved.
  const [queuedPolicy, setQueuedPolicy] = useState<SmsSessionPolicyByGroup | null>(null);

  const isPlatformTenant = partnerId === PLATFORM_PARTNER_ID;
  const policySetting = PartnerSetting.SmsSessionPolicyByGroup;
  const ownRecord = findPartnerSettingRecord(records, partnerId, policySetting);
  const platformValue = findPartnerSettingRecord(
    records,
    PLATFORM_PARTNER_ID,
    policySetting,
  )?.value;

  const ownPolicy = useMemo(() => readPolicy(ownRecord?.value) ?? {}, [ownRecord?.value]);
  const platformPolicy = useMemo(() => readPolicy(platformValue), [platformValue]);

  const entryIn = (policy: SmsSessionPolicyByGroup, groupKey: string): SmsSessionGroupPolicy =>
    resolveSmsSessionGroupPolicy(
      groupKey,
      isPlatformTenant
        ? { partner: null, platform: policy }
        : { partner: policy, platform: platformPolicy },
    );

  const shownPolicy = queuedPolicy ?? ownPolicy;

  const entryFor = (groupKey: string): SmsSessionGroupPolicy => entryIn(shownPolicy, groupKey);

  const isCustomized = (groupKey: string): boolean => shownPolicy[groupKey] !== undefined;

  const ownRecordId = (): string | undefined =>
    pending.current.recordId === undefined
      ? ownRecord?.partnerSettingValueId
      : (pending.current.recordId ?? undefined);

  function enqueueWrite(
    next: SmsSessionPolicyByGroup,
    write: () => Promise<boolean>,
  ): Promise<boolean> {
    const queue = pending.current;
    queue.policy = next;
    queue.count += 1;
    setQueuedPolicy(next);
    const run = queue.chain.then(write);
    queue.chain = run
      .catch(() => undefined)
      .then(() => {
        queue.count -= 1;
        if (queue.count === 0) {
          queue.policy = null;
          queue.recordId = undefined;
          setQueuedPolicy(null);
        }
      });
    return run;
  }

  /** Resolves true once the store holds `next`; a failed mutation resolves to no row. */
  async function writeOwnPolicy(next: SmsSessionPolicyByGroup): Promise<boolean> {
    const value = JSON.stringify(next);
    const recordId = ownRecordId();
    if (recordId) {
      const updated = await updatePartnerSettingsValue({
        partnerSettingValueId: recordId,
        partnerSettingId: PartnerSetting.SmsSessionPolicyByGroup,
        value,
      });
      pending.current.recordId = recordId;
      return Boolean(updated);
    }
    const created = await createPartnerSettingsValue({
      partnerSettingId: PartnerSetting.SmsSessionPolicyByGroup,
      value,
    });
    pending.current.recordId = created?.partnerSettingValueId ?? null;
    return Boolean(created);
  }

  /** Blurring an untouched field writes nothing: it must not pin the tenant to today's entry. */
  async function saveEntry(
    groupKey: string,
    patch: Partial<SmsSessionGroupPolicy>,
  ): Promise<void> {
    const base = pending.current.policy ?? ownPolicy;
    const current = entryIn(base, groupKey);
    const fields = Object.keys(patch) as (keyof SmsSessionGroupPolicy)[];
    if (fields.every((field) => patch[field] === current[field])) {
      return;
    }
    const next = { ...base, [groupKey]: { ...current, ...patch } };
    await enqueueWrite(next, () => writeOwnPolicy(next));
  }

  /** Drops the partner's entry for the group (the row goes with its last entry); false if the store kept it. */
  function resetEntry(groupKey: string): Promise<boolean> {
    const base = pending.current.policy ?? ownPolicy;
    const remaining = Object.fromEntries(
      Object.entries(base).filter(([key]) => key !== groupKey),
    );
    return enqueueWrite(remaining, async () => {
      if (Object.keys(remaining).length) {
        return writeOwnPolicy(remaining);
      }
      const recordId = ownRecordId();
      if (!recordId) {
        return true;
      }
      const deleted = await deletePartnerSettingsValue(recordId);
      if (deleted) {
        pending.current.recordId = null;
      }
      return Boolean(deleted);
    });
  }

  return { entryFor, isCustomized, saveEntry, resetEntry };
}

export function findPartnerSettingRecord(
  records: SettingRecord[],
  partnerId: number | null,
  settingId: PartnerSetting,
): SettingRecord | undefined {
  return records.find(
    (record) => record.partnerId === partnerId && record.partnerSettingId === settingId,
  );
}

/** A value that does not parse reads as empty, as the backend ignores it. */
function readPolicy(value: string | null | undefined): SmsSessionPolicyByGroup | null {
  if (!value) {
    return null;
  }
  try {
    return parseSmsSessionPolicyByGroup(value);
  } catch {
    return null;
  }
}
